import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, Filter, School } from 'lucide-react';
import { User } from '@/types';
import { supabase } from '@/integrations/supabase/client';

interface UserFiltersProps {
  users: User[];
  onFilter: (filteredUsers: User[]) => void;
}

export default function UserFilters({ users, onFilter }: UserFiltersProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [userRoles, setUserRoles] = useState<Map<string, string>>(new Map());
  const [availableSchools, setAvailableSchools] = useState<string[]>([]);

  useEffect(() => {
    const loadUserRoles = async () => {
      if (users.length === 0) return;
      
      try {
        const userIds = users.map(u => u.id);
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', userIds)
          .eq('is_active', true);

        const roleMap = new Map();
        rolesData?.forEach(role => {
          roleMap.set(role.user_id, role.role);
        });

        setUserRoles(roleMap);
      } catch (error) {
        console.error('Error loading user roles:', error);
      }
    };

    const loadSchools = () => {
      const schools = Array.from(new Set(users.map(user => user.school_name).filter(Boolean)));
      setAvailableSchools(schools);
    };

    loadUserRoles();
    loadSchools();
  }, [users]);

  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.position_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        const userRole = userRoles.get(user.id) || 'user';
        return userRole === roleFilter;
      });
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(user => user.department === departmentFilter);
    }

    if (schoolFilter !== 'all') {
      filtered = filtered.filter(user => user.school_name === schoolFilter);
    }

    onFilter(filtered);
  }, [users, searchTerm, roleFilter, departmentFilter, schoolFilter, userRoles, onFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setDepartmentFilter('all');
    setSchoolFilter('all');
  };

  // Get unique departments for filter
  const departments = Array.from(new Set(users.map(user => user.department).filter(Boolean)));

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search users..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="coordinator">Coordinator</SelectItem>
              <SelectItem value="principal">Principal</SelectItem>
              <SelectItem value="instructor">Instructor</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={schoolFilter} onValueChange={setSchoolFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by school" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Schools</SelectItem>
              {availableSchools.map(school => (
                <SelectItem key={school} value={school}>{school}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={clearFilters}>
            <Filter className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}